import * as Sentry from '@sentry/browser'

/**
 * FRONTEND-F (2026-11-04; 34 events, 29 at tranpenad): with the browser's page translation on (Chrome/Google
 * Translate wraps text in `<font>`, Edge's translator does the same kind of thing), React later removes or inserts
 * next to a text node the translator has moved, and the DOM throws "Failed to execute 'removeChild' on 'Node': The
 * node to be removed is not a child of this node." The page falls into the error boundary.
 *
 * The workaround from React's own issue (facebook/react#11538): when the node is no longer where React left it, skip
 * the DOM call instead of throwing. React's own view stays consistent; at worst a translated text keeps its old value
 * until the next render of that part. Each skip leaves a breadcrumb, so a real bug of this kind would still be
 * visible in the events that follow it.
 *
 * Translation keeps working (Stefan, 2026-11-04), rather than switching it off with `translate="no"`.
 */
export function installTranslationGuard(): void {
  if (typeof Node !== 'function' || !Node.prototype) return
  const proto = Node.prototype as Node & { __translationGuard?: boolean }
  if (proto.__translationGuard) return
  proto.__translationGuard = true

  const originalRemoveChild = proto.removeChild
  proto.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      Sentry.addBreadcrumb({
        category: 'dom.translation-guard',
        message: 'removeChild skipped: node moved',
      })
      return child
    }
    return originalRemoveChild.call(this, child) as T
  }

  const originalInsertBefore = proto.insertBefore
  proto.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      Sentry.addBreadcrumb({
        category: 'dom.translation-guard',
        message: 'insertBefore skipped: reference node moved',
      })
      return newNode
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T
  }
}
